'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { CALENDAR_EVENTS, CalendarEvent } from '@/lib/mockData';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onNext: () => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const catConfig: Record<CalendarEvent['category'], { label: string; cls: string; dot: string }> = {
  holiday: { label: 'Public Holiday', cls: 'cal-holiday', dot: 'bg-red-500' },
  school: { label: 'School Holiday', cls: 'cal-school', dot: 'bg-orange-400' },
  competition: { label: 'Competition', cls: 'cal-competition', dot: 'bg-blue-500' },
  special: { label: 'Special Event', cls: 'cal-special', dot: 'bg-purple-500' },
  maintenance: { label: 'Maintenance', cls: 'cal-closed', dot: 'bg-gray-400' },
};

function formatDateKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function buildEventMap(): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  CALENDAR_EVENTS.forEach((evt) => {
    const existing = map.get(evt.date) || [];
    map.set(evt.date, [...existing, evt]);
  });
  return map;
}

const EVENT_MAP = buildEventMap();

export default function BookingCalendar({ booking, updateBooking, onNext }: Props) {
  const today = new Date(2026, 5, 10);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [tooltip, setTooltip] = useState<{ date: string; events: CalendarEvent[] } | null>(null);

  const maxDate = new Date(today.getFullYear(), today.getMonth() + 24, 0);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  const canGoPrev = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());
  const canGoNext = !(viewYear === maxDate.getFullYear() && viewMonth === maxDate.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function handleDayClick(day: number) {
    const dateKey = formatDateKey(viewYear, viewMonth, day);
    const clickDate = new Date(viewYear, viewMonth, day);
    if (clickDate < today) return;
    if (clickDate > maxDate) return;
    const events = EVENT_MAP.get(dateKey) || [];
    const blocked = events.some(e => e.affectsBooking && (e.category === 'maintenance'));
    if (blocked) return;
    updateBooking({ selectedDate: dateKey });
    setTooltip({ date: dateKey, events });
  }

  function getDayStyle(day: number): string {
    const dateKey = formatDateKey(viewYear, viewMonth, day);
    const clickDate = new Date(viewYear, viewMonth, day);
    const isPast = clickDate < today;
    const events = EVENT_MAP.get(dateKey) || [];
    const isSelected = booking.selectedDate === dateKey;
    const isToday = dateKey === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    if (isSelected) return 'bg-primary text-white font-bold border-2 border-primary ring-2 ring-primary/30';
    if (isPast) return 'text-muted-foreground/40 cursor-not-allowed';
    if (events.some(e => e.category === 'holiday')) return 'cal-holiday cursor-pointer hover:opacity-80';
    if (events.some(e => e.category === 'school')) return 'cal-school cursor-pointer hover:opacity-80';
    if (events.some(e => e.category === 'competition')) return 'cal-competition cursor-pointer hover:opacity-80';
    if (events.some(e => e.category === 'special')) return 'cal-special cursor-pointer hover:opacity-80';
    if (isToday) return 'border-2 border-primary text-primary font-semibold cursor-pointer hover:bg-primary/5';
    return 'hover:bg-muted cursor-pointer text-foreground';
  }

  return (
    <div className="flex flex-col xl:grid xl:grid-cols-3 gap-4 sm:gap-6">
      {/* Calendar */}
      <div className="xl:col-span-2 bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-foreground">{MONTHS[viewMonth]} {viewYear}</h2>
          <div className="flex gap-2">
            <button
              onClick={prevMonth}
              disabled={!canGoPrev}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeftIcon" size={16} variant="outline" />
            </button>
            <button
              onClick={nextMonth}
              disabled={!canGoNext}
              className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronRightIcon" size={16} variant="outline" />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1 sm:mb-2">
          {DAYS.map((d) => (
            <div key={`day-header-${d}`} className="text-center text-xs font-semibold text-muted-foreground py-1.5 sm:py-2 uppercase tracking-wider">
              {d.charAt(0)}
              <span className="hidden sm:inline">{d.slice(1)}</span>
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {Array.from({ length: firstDay }, (_, i) => (
            <div key={`blank-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateKey = formatDateKey(viewYear, viewMonth, day);
            const events = EVENT_MAP.get(dateKey) || [];
            return (
              <button
                key={`day-${dateKey}`}
                onClick={() => handleDayClick(day)}
                className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-xs sm:text-sm transition-all duration-150 ${getDayStyle(day)}`}
              >
                <span className="font-tabular">{day}</span>
                {events.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {events.slice(0, 2).map((e, ei) => (
                      <div key={`dot-${dateKey}-${ei}`} className={`w-1 h-1 rounded-full ${catConfig[e.category]?.dot || 'bg-gray-400'}`} />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right Panel — stacked below on mobile */}
      <div className="flex flex-col sm:flex-row xl:flex-col gap-4">
        {/* Legend */}
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-card flex-1 xl:flex-none">
          <h3 className="font-semibold text-foreground text-sm mb-2 sm:mb-3">Calendar Legend</h3>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-1.5 sm:gap-2">
            {Object.entries(catConfig).map(([key, cfg]) => (
              <div key={`legend-${key}`} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded shrink-0 ${cfg.cls}`} />
                <span className="text-xs sm:text-sm text-muted-foreground">{cfg.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-primary shrink-0" />
              <span className="text-xs sm:text-sm text-muted-foreground">Selected Date</span>
            </div>
          </div>
        </div>

        {/* Selected Date Info */}
        {booking.selectedDate ? (
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-card fade-in flex-1 xl:flex-none">
            <h3 className="font-semibold text-foreground text-sm mb-2 sm:mb-3 flex items-center gap-2">
              <Icon name="CalendarDaysIcon" size={15} variant="outline" className="text-primary shrink-0" />
              <span className="truncate">{new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
            </h3>
            {(EVENT_MAP.get(booking.selectedDate) || []).length > 0 ? (
              <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                {(EVENT_MAP.get(booking.selectedDate) || []).map((e) => (
                  <div key={`sel-evt-${e.id}`} className={`px-3 py-2 rounded-lg text-xs font-medium ${catConfig[e.category]?.cls}`}>
                    <div className="font-semibold">{e.title}</div>
                    {e.description && <div className="mt-0.5 opacity-80 hidden sm:block">{e.description}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-3 sm:mb-4">No special events — tee sheet fully available.</p>
            )}
            <button
              onClick={onNext}
              className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            >
              View Tee Times
              <Icon name="ArrowRightIcon" size={16} variant="outline" />
            </button>
          </div>
        ) : (
          <div className="bg-muted/50 border border-border rounded-2xl p-4 sm:p-5 text-center flex-1 xl:flex-none flex flex-col items-center justify-center">
            <Icon name="CalendarDaysIcon" size={28} variant="outline" className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Select a date to see available tee times</p>
          </div>
        )}
      </div>
    </div>
  );
}